import type { MaybeRefOrGetter } from 'vue';
import { toValue } from 'vue';

export interface Point {
	x: number;
	y: number;
}

export type SubmenuSide = 'left' | 'right';

export interface UseSubmenuSafePathOptions {
	/** Rendered submenu bounds, read at each check so positioning and collision flips stay current. */
	submenuElement: MaybeRefOrGetter<HTMLElement | null | undefined>;
	/** Preferred side, used only when the pointer origin lies within the submenu's horizontal bounds. */
	side: MaybeRefOrGetter<SubmenuSide>;
	/**
	 * Horizontal progress toward the submenu must exceed vertical travel multiplied by this ratio.
	 * Defaults to `0`, which still requires movement toward the submenu; `0.5` rejects mostly vertical travel.
	 */
	minimumHorizontalIntentRatio?: MaybeRefOrGetter<number>;
}

/**
 * Tracks pointer movement from a parent menu item toward an open submenu.
 *
 * A safe path requires both movement toward the submenu and a point inside the triangle from the
 * last recorded pointer position to its near edge. Coordinates use viewport pixels (`clientX/Y`).
 *
 * Wire `handlePointerMove` to mouse movement, check `isWithinSafePath` before changing the active
 * item, and call `resetSafePath` when the submenu closes. A check must precede recording that same
 * position, otherwise there is no movement to compare. This helper installs no listeners or timers
 * and never changes selection itself; the caller decides whether to ignore a row-enter event.
 */
export function useSubmenuSafePath(options: UseSubmenuSafePathOptions) {
	let lastPointerPosition: Point | null = null;

	/** Stores the latest pointer position as the triangle origin for future leave checks. */
	function handlePointerMove(event: MouseEvent) {
		if (lastPointerPosition) {
			lastPointerPosition.x = event.clientX;
			lastPointerPosition.y = event.clientY;
		}
		else {
			lastPointerPosition = getEventPoint(event);
		}
	}

	/** Checks intent without advancing the origin; returns false without a recorded point or submenu. */
	function isWithinSafePath(event: MouseEvent): boolean {
		const origin = lastPointerPosition;
		const submenuElement = toValue(options.submenuElement);

		if (!origin || !submenuElement) return false;
		const point = getEventPoint(event);
		const submenuRect = submenuElement.getBoundingClientRect();
		const side = toValue(options.side);
		const minimumHorizontalIntentRatio = toValue(options.minimumHorizontalIntentRatio) ?? 0;

		if (!hasHorizontalIntent(point, origin, submenuRect, side, minimumHorizontalIntentRatio)) return false;

		return isPointInSubmenuSafePath(
			point,
			origin,
			submenuRect,
			side,
		);
	}

	/** Clears the current triangle origin when the submenu path is no longer relevant. */
	function resetSafePath() {
		lastPointerPosition = null;
	}

	return {
		handlePointerMove,
		isWithinSafePath,
		resetSafePath,
	};
}

/**
 * Checks whether `point` sits inside the triangle from `origin` to the submenu's near edge.
 *
 * The submenu can open on either side, and collision handling may flip it at runtime,
 * so the near edge is resolved from the actual submenu rect before falling back to the
 * preferred side.
 * Unlike `useSubmenuSafePath`, this geometry-only check does not require directional movement.
 */
export function isPointInSubmenuSafePath(point: Point, origin: Point, submenuRect: DOMRect, side: SubmenuSide): boolean {
	const submenuEdgeX = getSubmenuSafePathEdgeX(origin, submenuRect, side);

	return isPointInTriangle(
		point,
		origin,
		{ x: submenuEdgeX, y: submenuRect.top },
		{ x: submenuEdgeX, y: submenuRect.bottom },
	);
}

function hasHorizontalIntent(
	point: Point,
	origin: Point,
	submenuRect: DOMRect,
	side: SubmenuSide,
	minimumHorizontalIntentRatio: number,
): boolean {
	const submenuEdgeX = getSubmenuSafePathEdgeX(origin, submenuRect, side);
	const direction = Math.sign(submenuEdgeX - origin.x);
	const horizontalProgress = (point.x - origin.x) * direction;
	const verticalTravel = Math.abs(point.y - origin.y);

	return horizontalProgress > verticalTravel * minimumHorizontalIntentRatio;
}

function getEventPoint(event: MouseEvent): Point {
	return { x: event.clientX, y: event.clientY };
}

/**
 * Resolves the submenu edge that should form the far side of the safe triangle.
 *
 * Pointer origins outside the submenu bounds indicate the actual rendered side,
 * which is more reliable than the preferred side when the popover has flipped.
 */
function getSubmenuSafePathEdgeX(origin: Point, submenuRect: DOMRect, preferredSide: SubmenuSide): number {
	if (origin.x <= submenuRect.left) return submenuRect.left;
	if (origin.x >= submenuRect.right) return submenuRect.right;

	return preferredSide === 'right' ? submenuRect.left : submenuRect.right;
}

/** Uses signed edge tests to determine whether a point is inside a triangle. */
function isPointInTriangle(point: Point, a: Point, b: Point, c: Point): boolean {
	const d1 = sign(point, a, b);
	const d2 = sign(point, b, c);
	const d3 = sign(point, c, a);

	const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
	const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;

	return !(hasNegative && hasPositive);
}

/** Returns the signed area orientation for the point relative to a triangle edge. */
function sign(p1: Point, p2: Point, p3: Point): number {
	return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
}
